import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { toast } from '@dashboard/context/ToastContext';
import Button from '@dashboard/components/Button';
import { Button as ShadcnButton, buttonVariants } from '@dashboard/components/ui/button';
import Dialog from '@dashboard/components/Dialog';
import FileIcon from '@dashboard/components/FileIcon';
import Form from '@dashboard/components/Formik/Form';
import Reorderable from '@dashboard/components/Reorderable';
import clsx from 'clsx';
import { EllipsisVertical, GripVertical } from 'lucide-react';
import useFileQueries from '@dashboard/hooks/queries/useFileQueries';
import { confirm } from '@dashboard/lib/confirm';
import downloadUrlAsFile from '@dashboard/services/downloadUrlAsFile';
import logException from '@dashboard/services/logException';

import getFileSizeLabel from '../scenes/PageProduct/getFileSizeLabel';
import getFileTypeLabel from '../scenes/PageProduct/getFileTypeLabel';

function FileDownloadsList({
  files,
  onDeleteFile,
  onEditFile,
  onReorderFiles,
  getBadges,
  deleteDescription,
}) {
  if (!files?.length) {
    return null;
  }

  return (
    <div className="w-full border border-border rounded-md mb-4 bg-background overflow-hidden">
      <Reorderable.Wrapper>
        <Reorderable
          id="files"
          items={files}
          onReorder={onReorderFiles}
          renderItem={({ item: file, dragHandleProps, isDragging, index }) => (
            <Reorderable.Item isDragging={isDragging} border={index + 1 < files.length}>
              <FileListItem
                file={file}
                dragHandleProps={dragHandleProps}
                onDeleteFile={onDeleteFile}
                onEditFile={onEditFile}
                badges={getBadges?.(file)}
                deleteDescription={deleteDescription}
              />
            </Reorderable.Item>
          )}
        />
      </Reorderable.Wrapper>
    </div>
  );
}

function FileListItem({
  file,
  dragHandleProps,
  onDeleteFile,
  onEditFile,
  badges,
  deleteDescription,
}) {
  const { downloadFile, updateFile } = useFileQueries();
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [showProgress, setShowProgress] = useState(file.isUploading);

  useEffect(() => {
    if (file.isUploading) {
      setShowProgress(true);
    } else {
      const timer = setTimeout(() => setShowProgress(false), 500);
      return () => clearTimeout(timer);
    }
  }, [file.isUploading]);

  async function onDelete() {
    const del = await confirm('Delete this file?', {
      confirm: 'Delete',
      description: deleteDescription,
    });

    if (del) {
      try {
        await onDeleteFile(file);
      } catch (error) {
        logException(error);
        toast({
          title: 'Failed to delete file.',
          description: 'Please try again or contact support.',
          type: 'error',
        });
      }
    }
  }

  async function onDownload() {
    const response = await downloadFile(file._id);

    if (response?.data?.url) {
      downloadUrlAsFile(response.data.url, file.name);
    } else {
      toast({
        title: 'Unable to download file',
        description: 'Please try again or contact support.',
        type: 'error',
      });
    }
  }

  async function onRename(values) {
    try {
      const { data, error } = await updateFile(file._id, values);

      if (data) {
        onEditFile(file, data);
        setIsRenameOpen(false);
        return { data };
      }

      return { error };
    } catch (error) {
      return { error: error.message };
    }
  }

  return (
    <div>
      <div className="px-2 py-3">
        {/* The /upload variants only apply in the AI editor's narrow chat uploader. */}
        <div className="flex flex-row gap-3 items-center justify-start @max-[14rem]/upload:gap-2">
          <div
            {...dragHandleProps}
            className={clsx(
              buttonVariants({ variant: 'ghost', size: 'icon' }),
              'cursor-grab active:cursor-grabbing @max-[14rem]/upload:hidden',
            )}
          >
            <GripVertical className="size-4" />
          </div>

          <div className="flex items-center justify-center h-9 w-9 rounded-md bg-muted text-muted-foreground/70 shrink-0">
            <FileIcon type={file.type} className="h-4 w-4" />
          </div>

          <div className="min-w-0 truncate">
            <div className="text-sm font-medium text-foreground truncate" title={file.name}>
              {file.name}
            </div>
            <div className="text-xs text-muted-foreground">
              {getFileTypeLabel(file.type)
                ? `${getFileTypeLabel(file.type)} • ${getFileSizeLabel(file.size)}`
                : getFileSizeLabel(file.size)}
            </div>
          </div>

          <div className="flex ml-auto gap-2 items-center">
            <FileActions
              file={file}
              onEdit={() => setIsRenameOpen(true)}
              onDelete={onDelete}
              onDownload={onDownload}
            />
          </div>
        </div>

        {badges}
      </div>

      {showProgress && (
        <div className="relative w-full h-1 bg-muted">
          <div
            className="absolute h-full bg-foreground transition-all duration-300"
            style={{
              width: `${file.uploadProgress ?? 1}%`,
            }}
          />
        </div>
      )}

      <RenameFileDialog
        isOpen={isRenameOpen}
        onClose={() => setIsRenameOpen(false)}
        file={file}
        onRename={onRename}
      />
    </div>
  );
}

function RenameFileDialog({ isOpen, onClose, file, onRename }) {
  if (!file) return null;

  return (
    <Dialog
      open={isOpen}
      onCancel={onClose}
      title="Rename file"
      cancelButton={null}
      confirmButton={null}
    >
      <div className="h-full flex-1 flex flex-col mt-2">
        <Form
          showState={false}
          enableReinitialize
          initialValues={{ name: file.name }}
          validationSchema={Yup.object().shape({
            name: Yup.string().required('File name is required'),
          })}
          onSubmit={onRename}
          fields={[
            {
              name: 'name',
              label: 'File name',
              autoFocus: true,
            },
          ]}
          formSubmitAlign="end"
          cancelButton={{
            label: 'Cancel',
            onClick: onClose,
          }}
          submitButton={{
            label: 'Save',
          }}
        />
      </div>
    </Dialog>
  );
}

function FileActions({ onEdit, onDelete, onDownload, file }) {
  return (
    // Non-modal for the same reason as Images' menu: Delete's confirm dialog
    // would otherwise leave the page unclickable once it closes.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild disabled={!file._id}>
        <ShadcnButton variant="ghost" size="icon" aria-label="More options" disabled={!file._id}>
          <EllipsisVertical className="size-4" />
        </ShadcnButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onDownload}>Download</DropdownMenuItem>
        <DropdownMenuItem onSelect={onEdit}>Rename</DropdownMenuItem>
        <DropdownMenuItem onSelect={onDelete} variant="destructive">
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

FileDownloadsList.propTypes = {
  files: PropTypes.array,
  onDeleteFile: PropTypes.func.isRequired,
  onEditFile: PropTypes.func.isRequired,
  onReorderFiles: PropTypes.func.isRequired,
  getBadges: PropTypes.func,
  deleteDescription: PropTypes.string,
};

FileListItem.propTypes = {
  file: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    isUploading: PropTypes.bool,
    _id: PropTypes.string,
  }).isRequired,
  dragHandleProps: PropTypes.object,
  onDeleteFile: PropTypes.func.isRequired,
  onEditFile: PropTypes.func.isRequired,
  badges: PropTypes.node,
  deleteDescription: PropTypes.string,
};

RenameFileDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  file: PropTypes.shape({
    name: PropTypes.string,
    _id: PropTypes.string,
  }),
  onRename: PropTypes.func.isRequired,
};

FileActions.propTypes = {
  file: PropTypes.shape({
    _id: PropTypes.string,
  }),
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
};

export default FileDownloadsList;
